/** Catalog operations (spec §11). */

import { z } from "zod";
import { readOp, writeOp, zEpochMs, zId, zNonNegMoney } from "./common.js";

/**
 * B20a (ruling 2026-07-11): the axes carry their FULL §4.2 configuration —
 * a bare enum can name a WINDOW product but never say when the window is.
 * Shapes mirror packages/domain catalog types exactly.
 */
const zCapacityConfig = z.object({ model: z.enum(["NONE", "POOLED", "SLOTTED", "SEATED"]) }).strict();
const zValidityConfig = z.discriminatedUnion("model", [
  z.object({ model: z.literal("EVENT") }).strict(),
  z.object({ model: z.literal("WINDOW"), start: zEpochMs, end: zEpochMs }).strict(),
  z
    .object({
      model: z.literal("ROLLING"),
      windowMs: z.number().int().positive(),
      /** §2.2, Inv 7: what starts the clock. */
      activation: z.enum(["ON_VALIDITY_START", "ON_FIRST_USE"]),
    })
    .strict(),
  z.object({ model: z.literal("NONE") }).strict(),
]);
const zRedemptionConfig = z.discriminatedUnion("model", [
  z.object({ model: z.literal("SINGLE") }).strict(),
  z
    .object({
      model: z.literal("MULTI"),
      allowed: z.number().int().positive(),
      unit: z.enum(["PER_SCAN", "PER_SERVICE_DAY"]),
    })
    .strict(),
  z.object({ model: z.literal("UNLIMITED") }).strict(),
  z.object({ model: z.literal("NONE") }).strict(),
]);
const zAxes = z
  .object({ capacity: zCapacityConfig, validity: zValidityConfig, redemption: zRedemptionConfig })
  .strict();

/** §10.5: when revenue is earned. Absent = AT_SALE. */
const zRecognition = z.discriminatedUnion("policy", [
  z.object({ policy: z.literal("AT_SALE") }).strict(),
  z.object({ policy: z.literal("ON_REDEMPTION") }).strict(),
  z.object({ policy: z.literal("ON_EXPIRY") }).strict(),
  z.object({ policy: z.literal("RATABLE"), periods: z.number().int().positive() }).strict(),
]);

/** §2.4: variant × pool → units; 0 units is legitimate configuration (lap infants). */
const zConsumption = z.record(z.string().min(1), z.number().int().nonnegative());
const zVariantConstraints = z
  .object({
    minPerOrder: z.number().int().positive().optional(),
    maxPerOrder: z.number().int().positive().optional(),
    /** "Child requires an accompanying Adult in the same order" (§4.2). */
    requiresVariantId: zId.optional(),
  })
  .strict();
/** B614 (b2) — a ticket type's one-line meaning for buyers ("Seated first, closest to the stage"). */
const zVariantDescription = z.string().min(1).max(140).regex(/\S/, "a ticket description must not be blank");
const zVariant = z
  .object({
    variantId: zId,
    name: z.string().min(1),
    consumption: zConsumption,
    constraints: zVariantConstraints.optional(),
    description: zVariantDescription.optional(),
  })
  .strict();

/** B57: a journey endpoint as the buyer sees it — stop key + display name. */
const zJourneyStop = z.object({ key: z.string().min(1), name: z.string().min(1) }).strict();
/**
 * B57: O-D display metadata for derived route products (spec §16 E1) —
 * named endpoints + the ordered leg keys (`<from>~<to>`, origin first) the
 * journey rides. Presentation ONLY: the §2.4 consumption matrix stays off
 * the buyer card (B20b(a)); cart.add/checkout remain the walls.
 */
const zJourney = z
  .object({
    from: zJourneyStop,
    to: zJourneyStop,
    legs: z.array(z.string().regex(/^[^~]+~[^~]+$/)).min(1),
  })
  .strict();
/**
 * B31h (TRA-80): buyer-flow order; B57: journey descriptor. Presentation-
 * layer config only — both fields optional ({} is legal and inert: the MCP
 * membrane translates plain objects only, so no refine may guard this).
 */
/** B383 (RULED 2026-08-19): a server-owned POS grid grouping - opaque id + operator-facing label. */
const zPresentationGroup = z.object({ id: zId, label: z.string().min(1) }).strict();
/** B521 (ruled 2026-09-22): one product photo — https only, never hot-linked http. */
const zPhoto = z
  .object({
    url: z
      .string()
      .url()
      .regex(/^https:\/\//i, "a product photo must be an https URL"),
    alt: z.string().min(1).max(200).optional(),
    credit: z.string().min(1).max(120).optional(),
  })
  .strict();
/**
 * B615 — a show's policies as FACTS, not prose: who may come (minAge), what
 * the house asks (drinkMinimum), whether a sale can be undone, when doors
 * open and when unclaimed seats go. Strict — an unknown policy is refused.
 */
const zPolicies = z
  .object({
    minAge: z.number().int().min(1).max(99).optional(),
    drinkMinimum: z.number().int().min(1).max(10).optional(),
    refundPolicy: z.enum(["FINAL", "STANDARD"]).optional(),
    doorsOpenMinutes: z.number().int().min(0).max(240).optional(),
    lateSeatingMinutes: z.number().int().min(0).max(240).optional(),
    houseRules: z.array(z.string().min(1).max(140).regex(/\S/, "a house rule must not be blank")).min(1).max(10).optional(),
  })
  .strict();
/** B615 — the policy facts as the composer reads them (zPolicies' shape, readonly-friendly). */
export interface PolicyFacts {
  readonly minAge?: number;
  readonly drinkMinimum?: number;
  readonly refundPolicy?: "FINAL" | "STANDARD";
  readonly doorsOpenMinutes?: number;
  readonly lateSeatingMinutes?: number;
  readonly houseRules?: readonly string[];
}

/**
 * B615 leg 2 — the ONE line a buyer ticks on the pay step, composed from the
 * cart's products' policy facts; `undefined` when nothing there asks for
 * agreement (doors and house rules inform, they are not agreed to). Across
 * products the strictest fact speaks: the highest age, any final sale, the
 * biggest drink minimum, the earliest seat release. One grammar for both
 * sides — the storefront draws it, the server records it (never client prose).
 */
export function policyAcknowledgement(all: readonly (PolicyFacts | undefined)[]): string | undefined {
  let minAge: number | undefined;
  let drinkMinimum: number | undefined;
  let lateSeating: number | undefined;
  let final = false;
  for (const p of all) {
    if (p === undefined) continue;
    if (p.minAge !== undefined) minAge = Math.max(minAge ?? 0, p.minAge);
    if (p.drinkMinimum !== undefined) drinkMinimum = Math.max(drinkMinimum ?? 0, p.drinkMinimum);
    if (p.lateSeatingMinutes !== undefined) lateSeating = Math.min(lateSeating ?? Infinity, p.lateSeatingMinutes);
    if (p.refundPolicy === "FINAL") final = true;
  }
  const clauses: string[] = [];
  if (final) clauses.push("all sales are final");
  if (drinkMinimum !== undefined) clauses.push(`there’s a ${drinkMinimum}-drink minimum per guest`);
  if (lateSeating !== undefined)
    clauses.push(lateSeating === 0 ? "seats are released at the start time" : `seats are released ${lateSeating} minutes after the start time`);
  const understood =
    clauses.length === 0
      ? undefined
      : clauses.length === 1
        ? clauses[0]!
        : clauses.length === 2
          ? `${clauses[0]} and ${clauses[1]}`
          : `${clauses.slice(0, -1).join(", ")}, and ${clauses[clauses.length - 1]}`;
  if (minAge === undefined) return understood === undefined ? undefined : `I understand ${understood}.`;
  return understood === undefined ? `I’m ${minAge} or older.` : `I’m ${minAge} or older and understand ${understood}.`;
}

/**
 * B617 — a show's lineup as NAMES on the product (the performer entity —
 * roster, bio, pages — is the later item): who headlines, who features,
 * who hosts. Strict: a lineup without a headliner is refused.
 */
const zPerformerName = z.string().min(1).max(120).regex(/\S/, "a name must not be blank");
export const zLineup = z
  .object({
    headliner: zPerformerName,
    features: z.array(zPerformerName).min(1).max(6).optional(),
    host: zPerformerName.optional(),
  })
  .strict();
/** B617 — the lineup as the composer reads it (zLineup's shape, readonly-friendly). */
export interface LineupNames {
  readonly headliner: string;
  readonly features?: readonly string[];
  readonly host?: string;
}

/**
 * B617 — the rest of the bill in ONE line, under the headliner: "with A and B
 * · hosted by C". `undefined` when the headliner plays alone. One grammar for
 * every face (poster, tickets step, email) so none words it twice.
 */
export function lineupLine(lineup: LineupNames): string | undefined {
  const f = lineup.features ?? [];
  const featuring =
    f.length === 0 ? undefined : f.length === 1 ? `with ${f[0]!}` : `with ${f.slice(0, -1).join(", ")} and ${f[f.length - 1]!}`;
  const host = lineup.host !== undefined ? `hosted by ${lineup.host}` : undefined;
  const parts = [featuring, host].filter((x): x is string => x !== undefined);
  return parts.length === 0 ? undefined : parts.join(" \u00b7 ");
}

const zPresentation = z
  .object({
    flowOrder: z.enum(["SLOT_FIRST", "TICKETS_FIRST"]).optional(),
    journey: zJourney.optional(),
    /** B383: which POS grid tab the product lives under; absent = the till's "All tickets" fallback. */
    group: zPresentationGroup.optional(),
    /** B383: opaque image reference for the product tile; absent = the generic tile. */
    imageRef: z.string().min(1).optional(),
    /** B521: https photos on the tenant asset host, ≤6; photos[0] is the tile. */
    photos: z.array(zPhoto).max(6).optional(),
    /** B521: plain text, ≤600 chars (the domain also refuses markup at publish). */
    description: z.string().min(1).max(600).regex(/\S/, "a description must not be blank").optional(),
    /** B521: the demo builder's citation (page path it lifted the text from). Operator-only. */
    descriptionSource: z.string().min(1).max(300).optional(),
    /** B615: the show's policies as facts (badges, the checkout acknowledgement, the ticket, the door). */
    policies: zPolicies.optional(),
    /** B617: the show's lineup as names (the poster, the tickets step, the email subject). */
    lineup: zLineup.optional(),
  })
  .strict();
/**
 * B521: the BUYER card's presentation — the stored shape plus `summary`, the
 * description's first sentence, DERIVED server-side (never written: the write
 * side keeps the strict zPresentation, so a client cannot author a summary
 * that disagrees with its own description).
 */
const zCardPresentation = zPresentation.extend({ summary: z.string().min(1).optional() }).strict();

const zProductInput = z
  .object({
    productId: zId,
    name: z.string().min(1),
    axes: zAxes,
    /** Optional at create (drafts may grow); publish still refuses variant-less products. */
    variants: z.array(zVariant).optional(),
    /** §10.3: flat per-category tax rates; omitted = never taxed. */
    taxCategory: z.string().min(1).optional(),
    recognition: zRecognition.optional(),
    /** B31h: which order the storefront walks the buyer through. */
    presentation: zPresentation.optional(),
  })
  .strict();
const exampleAxes = {
  capacity: { model: "SLOTTED" },
  validity: { model: "EVENT" },
  redemption: { model: "SINGLE" },
};
const exampleVariants = [
  { variantId: "adult", name: "Adult", consumption: { seats: 1 } },
  {
    variantId: "vehicle",
    name: "Car + driver",
    consumption: { seats: 1, "vehicle-deck": 1 },
    constraints: { maxPerOrder: 1 },
  },
];

export const productCreate = writeOp({
  name: "product.create",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  fields: { product: zProductInput },
  response: z.object({ productId: zId, status: z.literal("DRAFT") }).strict(),
  exampleRequest: {
    product: { productId: "p-1", name: "Ferry crossing", axes: exampleAxes, variants: exampleVariants },
  },
  exampleResponse: { productId: "p-1", status: "DRAFT" },
});

export const productUpdate = writeOp({
  name: "product.update",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    productId: zId,
    patch: z
      .object({
        name: z.string().min(1).optional(),
        axes: zAxes.optional(),
        variants: z.array(zVariant).optional(),
        taxCategory: z.string().min(1).optional(),
        recognition: zRecognition.optional(),
        presentation: zPresentation.optional(),
      })
      .strict(),
  },
  response: z.object({ productId: zId, status: z.enum(["DRAFT", "ACTIVE", "RETIRED"]) }).strict(),
  exampleRequest: { productId: "p-1", patch: { variants: exampleVariants } },
  exampleResponse: { productId: "p-1", status: "DRAFT" },
});

/**
 * B510 (class-B default, 2026-09-24) - the retire door: withdraw the OFFER,
 * never the sales. Flips the row RETIRED (the roster's existing filter), and
 * because buyers price off each channel's PINNED frozen catalogVersion
 * (Inv 11 - the row flip alone withdraws nothing), publishes a successor
 * version without the product for every channel whose pinned version carries
 * it and repoints (the B423 pointer move, priceLists untouched). Refuses
 * while the product still SELLS: pinned somewhere AND a future ON_SALE slot
 * binds it (the B31c coverage rule) - naming the slots. Sold tickets stay
 * valid; the section 5 state machine and the ledger are untouched.
 * Reversal: republish a version carrying the product (B425 repoint).
 */
export const productRetire = writeOp({
  name: "product.retire",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  fields: { productId: zId },
  response: z
    .object({
      productId: zId,
      status: z.literal("RETIRED"),
      /** One row per repointed channel; channels pinned to the same prior version share a successor. */
      successors: z.array(z.object({ channelId: zId, versionId: zId, publishedAt: zEpochMs }).strict()),
    })
    .strict(),
  exampleRequest: { productId: "p-1" },
  exampleResponse: {
    productId: "p-1",
    status: "RETIRED",
    successors: [{ channelId: "web", versionId: "v-2", publishedAt: 1_780_000_000_000 }],
  },
});

/**
 * B521 (RULED 2026-09-23) — the photo UPLOAD door: a 5-minute presigned PUT
 * of an ORIGINAL to the tenant asset host. The key is minted server-side under
 * this tenant's product (never caller-named); `content-type` and the exact
 * `content-length` ride as SIGNED headers, so the browser cannot swap either.
 * The admin then calls product.photo.complete and saves the answered URL on
 * the product (product.update's presentation.photos).
 */
export const productPhotoUpload = writeOp({
  name: "product.photo.upload",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    productId: zId,
    contentType: z.enum(["image/jpeg", "image/png", "image/webp"]),
    /** ≤ 5 MB — the ruled cap; the signed length makes it binding at S3. */
    bytes: z.number().int().positive().max(5 * 1024 * 1024),
  },
  response: z
    .object({
      key: z.string().min(1),
      upload: z
        .object({
          url: z.string().url(),
          method: z.literal("PUT"),
          headers: z.record(z.string(), z.string()),
        })
        .strict(),
      expiresAt: zEpochMs,
    })
    .strict(),
  exampleRequest: { productId: "p-1", contentType: "image/jpeg", bytes: 1_200_000 },
  exampleResponse: {
    key: "tenants/tenant-1/products/p-1/a1b2c3d4.jpg",
    upload: {
      url: "https://assets.example.com/src/tenants/tenant-1/products/p-1/a1b2c3d4.jpg?X-Amz-Signature=…",
      method: "PUT",
      headers: { "content-type": "image/jpeg", "content-length": "1200000" },
    },
    expiresAt: 1_780_000_300_000,
  },
});

/**
 * B521 — the upload's completion: the ingest guard measures the landed
 * original (downscales one over 4000px once; refuses + deletes what is not a
 * JPEG/PNG/WebP photo) and answers the photo's asset-host URL.
 */
export const productPhotoComplete = writeOp({
  name: "product.photo.complete",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  fields: { productId: zId, key: z.string().min(1).max(400) },
  response: z
    .object({
      photo: z.object({ url: z.string().url().regex(/^https:\/\//) }).strict(),
      width: z.number().int().positive(),
      height: z.number().int().positive(),
      downscaled: z.boolean(),
    })
    .strict(),
  exampleRequest: { productId: "p-1", key: "tenants/tenant-1/products/p-1/a1b2c3d4.jpg" },
  exampleResponse: {
    photo: { url: "https://assets.example.com/i/tenants/tenant-1/products/p-1/a1b2c3d4.jpg" },
    width: 4000,
    height: 2667,
    downscaled: true,
  },
});

export const productPublish = writeOp({
  name: "product.publish",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  /* B423 (ruled 2026-09-04): naming `channels` repoints each channel's live
     commerce-config catalogVersion to the just-published version in the same
     operation (priceLists untouched); the frozen version carries the channel's
     current roster alongside the named products, so a launch never shrinks a
     shop window. Reversal: republish the prior versionId — B425 made that
     sentence real: naming an EXISTING frozen versionId with channels is a
     pointer REPOINT (no version written, original publishedAt answered,
     every named productId must be frozen in that version). */
  fields: { versionId: zId, productIds: z.array(zId).min(1), channels: z.array(zId).min(1).optional() },
  response: z.object({ versionId: zId, publishedAt: zEpochMs }).strict(),
  exampleRequest: { versionId: "v-1", productIds: ["p-1"] },
  exampleResponse: { versionId: "v-1", publishedAt: 1_780_000_000_000 },
});

/**
 * B20b(a) ruling 2026-07-14: the catalog reads are the buyer's shop window
 * (§7 step 1, §10 display, §2.4). Cards carry the full axes and every
 * variant with its constraints and an optional channel-resolved display
 * price — displayPrice is present only when the channel's price lists
 * resolve the exact key (honest absence, Inv 11 groundwork). The §2.4
 * consumption matrix deliberately stays OUT of the buyer read — the same
 * stance as slot.availability never exposing raw sold/held.
 */
const zCardVariant = z
  .object({
    variantId: zId,
    name: z.string().min(1),
    constraints: zVariantConstraints.optional(),
    displayPrice: zNonNegMoney.optional(),
    /** B614 (b2) — the type's one-line meaning, when the operator wrote one. */
    description: zVariantDescription.optional(),
  })
  .strict();
/**
 * B31c amendment (2026-07-16, TRA-52; §4.3 schedules, §7 step 1, §2.4/§6
 * pool binding): the card carries the product→schedule binding so the
 * storefront can list departures. A schedule qualifies iff it is PUBLISHED
 * and its slot pool template covers the union of the product's consumed
 * pool keys — exactly the slots cart.add will accept. [] = no departure
 * step (non-SLOTTED, or nothing bindable). Possibly several (multi-schedule
 * products list all of them).
 */
const zProductCard = z
  .object({
    productId: zId,
    name: z.string().min(1),
    axes: zAxes,
    scheduleIds: z.array(zId),
    variants: z.array(zCardVariant),
    /** B31h: rides the card verbatim; absent = storefront default (SLOT_FIRST). B521: + the derived summary. */
    presentation: zCardPresentation.optional(),
  })
  .strict();
const exampleCard = {
  productId: "p-1",
  name: "Ferry crossing",
  axes: exampleAxes,
  scheduleIds: ["sch-1"],
  variants: [
    { variantId: "adult", name: "Adult", displayPrice: { amount: 5000, currency: "USD" } },
    { variantId: "vehicle", name: "Car + driver", constraints: { maxPerOrder: 1 } },
  ],
};

/** channelId resolves the active version AND prices; versionId pins a specific frozen version. One of the two is required — the handler refuses a request carrying neither. */
export const catalogList = readOp({
  name: "catalog.list",
  permission: "catalog:read",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: { versionId: zId.optional(), channelId: zId.optional() },
  response: z.object({ versionId: zId, products: z.array(zProductCard) }).strict(),
  exampleRequest: { channelId: "web" },
  exampleResponse: { versionId: "v-1", products: [exampleCard] },
});

/**
 * B71 — the OPERATOR-grade read: the raw product record, including variant
 * consumption (§2.4) that the buyer card is walled from (B20b). Back-office
 * audience only; the admin editor loads from here so Edit round-trips the
 * whole product instead of clobbering what the card cannot see.
 */
const zProductFull = z
  .object({
    productId: zId,
    name: z.string().min(1),
    status: z.enum(["DRAFT", "ACTIVE", "RETIRED"]),
    axes: zAxes,
    variants: z.array(zVariant),
    taxCategory: z.string().min(1).optional(),
    recognition: zRecognition.optional(),
    presentation: zPresentation.optional(),
  })
  .strict();

export const productGet = readOp({
  name: "product.get",
  permission: "catalog:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { productId: zId },
  response: z.object({ product: zProductFull.optional() }).strict(),
  exampleRequest: { productId: "p-1" },
  exampleResponse: {
    product: { productId: "p-1", name: "Ferry crossing", status: "ACTIVE", axes: exampleAxes, variants: exampleVariants },
  },
});

/**
 * B156 — the staff roster read behind the setup hub: DRAFT and ACTIVE rows
 * off the store of record, so a tenant mid-walk sees a saved draft from any
 * device. Card-shaped on purpose (schedule bindings + channel-resolved
 * display prices when a channelId is offered) so the hub derives its facts
 * from one vocabulary — and NEVER the §2.4 consumption matrix (that stays
 * product.get's, B20b). RETIRED rows are not on the roster: the schema
 * itself refuses the status.
 */
const zProductListRow = z
  .object({
    productId: zId,
    name: z.string().min(1),
    status: z.enum(["DRAFT", "ACTIVE"]),
    scheduleIds: z.array(zId),
    variants: z.array(
      z.object({ variantId: zId, name: z.string().min(1), displayPrice: zNonNegMoney.optional() }).strict(),
    ),
  })
  .strict();

export const productList = readOp({
  name: "product.list",
  permission: "catalog:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { channelId: zId.optional() },
  response: z
    .object({
      products: z.array(zProductListRow),
      /**
       * B216 — the tenant's CHANNEL roster, ordered by id. Absent exactly
       * when `taxCategories` is absent (no commerce wiring), so a caller
       * never has to reason about one without the other. It is what makes
       * "this category is configured everywhere" answerable: without the
       * roster, a category carrying one channel's rate and a category
       * carrying the only channel's rate are indistinguishable.
       */
      channels: z.array(zId).optional(),
      /**
       * B214 — the tenant's configured tax categories, so the product
       * editor's tax field can be a picker fed by real data (FORMS.md R4)
       * rather than free text whose typo becomes a TaxConfigError at quote
       * time (pricing.ts:388). These are the SAME rates priceCart builds
       * rateByCategory from, so an offered option and a priceable category
       * cannot drift apart.
       *
       * B216 — the roster spans every channel the tenant configures, not
       * the channel in the request. A config row is per (tenant, channel)
       * by design, and a product is not channel-scoped, so following the
       * requested channel offered one channel's categories for a product
       * every channel sells. Each category is offered ONCE and carries
       * every channel's own rate: the same category NAME may price
       * differently per channel, and a single collapsed rateBps would be a
       * lie the moment two channels disagree. `rates` is never empty — a
       * category exists here because some channel configured it — and the
       * derived facts a face needs (configured everywhere? one agreed
       * rate?) are computed from `rates` + `channels` rather than asserted
       * in a second field that could contradict the first.
       *
       * ABSENT and EMPTY still mean different things and callers must keep
       * them apart: absent = no commerce wiring, the roster is UNKNOWN;
       * empty = the roster was read and this tenant configures no rates. A
       * face that renders both the same claims a fact it never read.
       */
      taxCategories: z
        .array(
          z
            .object({
              category: z.string().min(1),
              rates: z
                .array(
                  z
                    .object({
                      channelId: zId,
                      rateBps: z.number().int().nonnegative(),
                      inclusive: z.boolean(),
                    })
                    .strict(),
                )
                .min(1),
            })
            .strict(),
        )
        .optional(),
    })
    .strict(),
  exampleRequest: { channelId: "web" },
  exampleResponse: {
    products: [
      { productId: "p-1", name: "Ferry crossing", status: "DRAFT", scheduleIds: [], variants: [{ variantId: "adult", name: "Adult" }] },
    ],
    channels: ["web"],
    taxCategories: [{ category: "standard", rates: [{ channelId: "web", rateBps: 2000, inclusive: false }] }],
  },
});

export const catalogGet = readOp({
  name: "catalog.get",
  permission: "catalog:read",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: { versionId: zId.optional(), productId: zId, channelId: zId.optional() },
  response: z.object({ product: zProductCard.optional() }).strict(),
  exampleRequest: { versionId: "v-1", productId: "p-1" },
  exampleResponse: { product: exampleCard },
});

/**
 * B118 slice 3 — the Settings history roster: every published version,
 * when it went live, and how many products it carried. The count is
 * derived HERE so the screen never meets a product list it does not
 * render — and the operator never meets a version id (FORMS.md R7);
 * versionId travels for row identity only. Back-office truth, same
 * seat as pricelist.list.
 */
/**
 * B521 open leg 2 — the buyer calendar's per-day "from" prices, computed
 * from the product-scoped TIME pricing rule where one exists. The honesty
 * contract rides the shape: FLAT carries ONE number (the storefront draws
 * it beside the selected day only), TIME carries per-slot computed prices —
 * a per-day figure can only exist where a day rule actually does. Slot-
 * scoped policies stay a quote-time concern; the figure is "from".
 */
export const productCalendar = readOp({
  name: "product.calendar",
  permission: "catalog:read",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:KIOSK"],
  fields: { productId: zId, channelId: zId, from: zEpochMs.optional(), to: zEpochMs.optional() },
  response: z
    .object({
      pricing: z
        .discriminatedUnion("kind", [
          z.object({ kind: z.literal("FLAT"), from: zNonNegMoney }).strict(),
          z
            .object({
              kind: z.literal("TIME"),
              slots: z.array(z.object({ slotId: zId, start: zEpochMs, from: zNonNegMoney }).strict()),
            })
            .strict(),
        ])
        .optional(),
    })
    .strict(),
  exampleRequest: { productId: "p-1", channelId: "web" },
  exampleResponse: { pricing: { kind: "FLAT", from: { amount: 2500, currency: "USD" } } },
});

export const catalogVersionList = readOp({
  name: "catalog.version.list",
  permission: "catalog:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {},
  response: z
    .object({
      versions: z.array(
        z
          .object({
            versionId: zId,
            /** When buyers started seeing this version (epoch ms). */
            publishedAt: zEpochMs,
            /** Products on sale in this version. */
            productCount: z.number().int().nonnegative(),
          })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: { versions: [{ versionId: "v-1", publishedAt: 1_780_000_000_000, productCount: 7 }] },
});

/**
 * B20a (ruling 2026-07-11): pricing writes remodeled per §4.2 to match
 * B9's resolution exactly. The LIST owns currency (one per tenant, §10.1),
 * channel binding (absent = the default list) and effective windows;
 * a price row is an exact (productId, variantId) key in the list's
 * currency. Pre-B20a shapes (per-price channel + effectiveFrom, optional
 * variantId, currency-less lists) forced invented quote semantics (Inv 11).
 */
export const pricelistCreate = writeOp({
  name: "pricelist.create",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    priceListId: zId,
    name: z.string().min(1),
    /** ISO 4217; B9 enforces one currency per tenant at quote time (§10.1). */
    currency: z.string().regex(/^[A-Z]{3}$/),
    /** Absent = the tenant's default list. */
    channelId: zId.optional(),
    /** Epoch ms, inclusive start / exclusive end; omitted = unbounded. */
    effectiveFrom: zEpochMs.optional(),
    effectiveTo: zEpochMs.optional(),
  },
  response: z.object({ priceListId: zId }).strict(),
  exampleRequest: { priceListId: "pl-1", name: "Web standard", currency: "USD", channelId: "web" },
  exampleResponse: { priceListId: "pl-1" },
});

/**
 * B118 — the roster the admin's rebuilt price-list section renders: every
 * list with its quote-shaping facts plus the two derived columns the
 * screen needs (distinct products priced, when the list was minted).
 * Back-office truth, same seat as promotion.list.
 */
export const pricelistList = readOp({
  name: "pricelist.list",
  permission: "catalog:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {},
  response: z
    .object({
      priceLists: z.array(
        z
          .object({
            priceListId: zId,
            name: z.string(),
            currency: z.string(),
            /** Absent = the tenant's default list. */
            channelId: zId.optional(),
            effectiveFrom: zEpochMs.optional(),
            effectiveTo: zEpochMs.optional(),
            /** Distinct products with at least one price row on this list. */
            productsPriced: z.number().int().nonnegative(),
            /** When the list was minted (epoch ms). */
            inUseSince: zEpochMs,
            /**
             * B646 (b) — the list's own rows, sorted by product then ticket
             * type, so the list's page can edit them. Optional: a deploy
             * older than B646 answers without them.
             */
            prices: z
              .array(z.object({ productId: zId, variantId: zId, amount: z.number().int().safe().nonnegative() }).strict())
              .optional(),
          })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    priceLists: [
      {
        priceListId: "pl-1",
        name: "Web standard",
        currency: "USD",
        channelId: "web",
        productsPriced: 1,
        inUseSince: 1_780_000_000_000,
        prices: [{ productId: "p-1", variantId: "adult", amount: 5000 }],
      },
    ],
  },
});

export const pricelistUpdate = writeOp({
  name: "pricelist.update",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  /** Currency and channel binding are quote-shaping and immutable — create a new list instead. */
  fields: {
    priceListId: zId,
    patch: z
      .object({
        name: z.string().min(1).optional(),
        effectiveFrom: zEpochMs.optional(),
        effectiveTo: zEpochMs.optional(),
      })
      .strict(),
  },
  response: z.object({ priceListId: zId }).strict(),
  exampleRequest: { priceListId: "pl-1", patch: { name: "Web summer" } },
  exampleResponse: { priceListId: "pl-1" },
});

export const priceSet = writeOp({
  name: "price.set",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    priceListId: zId,
    productId: zId,
    /** Required — B9 resolves exact (productId, variantId) keys only. */
    variantId: zId,
    /** Minor units in the list's currency; §11: never negative. */
    amount: z.number().int().safe().nonnegative(),
  },
  response: z.object({ priceId: zId }).strict(),
  exampleRequest: { priceListId: "pl-1", productId: "p-1", variantId: "adult", amount: 5000 },
  exampleResponse: { priceId: "price-1" },
});

/**
 * B20a (ruling 2026-07-11): benefits carry their magnitudes and the
 * eligibility predicate arrives in full — mirrors B10's domain shapes
 * (§10.2). Pre-B20a the benefit was a bare enum: a PERCENT_OFF had no
 * percent. Cross-field rules (X_FOR_Y needs minQuantity ≥ 2, window
 * ordering) stay domain validation — the contract carries the data.
 */
const zBenefit = z.discriminatedUnion("type", [
  z.object({ type: z.literal("PERCENT_OFF"), bps: z.number().int().min(1).max(10_000) }).strict(),
  /** Minor units, positive — a zero discount is not a promotion. */
  z.object({ type: z.literal("AMOUNT_OFF"), amount: z.number().int().positive() }).strict(),
  z.object({ type: z.literal("FEE_WAIVER"), feeId: zId }).strict(),
  /** Cheapest eligible unit free — buy-3-get-1 is X_FOR_Y with eligibility.minQuantity 4. */
  z.object({ type: z.literal("X_FOR_Y") }).strict(),
  /** The comp: MANAGER-gated override to a per-unit amount, mandatory reason code (§10.2). */
  z
    .object({
      type: z.literal("PRICE_OVERRIDE"),
      amount: z.number().int().nonnegative(),
      reasonCode: z.string().min(1),
    })
    .strict(),
]);

const zEligibility = z
  .object({
    productIds: z.array(zId).optional(),
    /** priceKey(productId, variantId) scope, finer than productIds. */
    variantKeys: z.array(z.string().min(1)).optional(),
    channelIds: z.array(zId).optional(),
    window: z.object({ start: zEpochMs, end: zEpochMs }).strict().optional(),
    /** Total eligible units in the cart. */
    minQuantity: z.number().int().positive().optional(),
    /** Pre-discount spend on eligible lines, minor units. */
    minSpendMinor: z.number().int().positive().optional(),
    /** Present = code-triggered; absent = automatic. */
    code: z.string().min(1).optional(),
    perCustomerCap: z.number().int().positive().optional(),
    totalCap: z.number().int().positive().optional(),
  })
  .strict();

const zPromotionInput = z
  .object({
    promotionId: zId,
    name: z.string().min(1),
    eligibility: zEligibility,
    benefit: zBenefit,
  })
  .strict();

export const promotionCreate = writeOp({
  name: "promotion.create",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  fields: { promotion: zPromotionInput },
  response: z.object({ promotionId: zId }).strict(),
  exampleRequest: {
    promotion: {
      promotionId: "promo-1",
      name: "Summer 10",
      eligibility: { code: "SUMMER10", channelIds: ["web"], totalCap: 1000 },
      benefit: { type: "PERCENT_OFF", bps: 1000 },
    },
  },
  exampleResponse: { promotionId: "promo-1" },
});

export const promotionUpdate = writeOp({
  name: "promotion.update",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  /* B647 — the patch takes the whole deal (name, eligibility, benefit —
     each replaced whole, the create's own shapes); it prices NEW orders
     only. A code another live promotion holds is refused (409). */
  fields: {
    promotionId: zId,
    patch: z.object({ name: z.string().min(1).optional(), eligibility: zEligibility.optional(), benefit: zBenefit.optional() }).strict(),
  },
  response: z.object({ promotionId: zId }).strict(),
  exampleRequest: { promotionId: "promo-1", patch: { name: "Summer 15", benefit: { type: "PERCENT_OFF", bps: 1500 } } },
  exampleResponse: { promotionId: "promo-1" },
});

export const promotionDisable = writeOp({
  name: "promotion.disable",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  fields: { promotionId: zId },
  response: z.object({ promotionId: zId, disabled: z.literal(true) }).strict(),
  exampleRequest: { promotionId: "promo-1" },
  exampleResponse: { promotionId: "promo-1", disabled: true },
});

/**
 * B288 — promotion.enable: the return door promotion.disable turned out to
 * need. "One-way" was the Phase-1 shape until FERRY10 died in a click-test
 * and no honest path could bring it back (a re-seed replays on Inv 4 and
 * the seed skips disabled rows by design). A promotion is CONFIGURATION,
 * so the reversal of disable is enable — idempotent, ADMIN-gated like its
 * twin, and it touches NEW orders only (confirmed orders keep the frozen
 * facts they already have).
 */
export const promotionEnable = writeOp({
  name: "promotion.enable",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  fields: { promotionId: zId },
  response: z.object({ promotionId: zId, disabled: z.literal(false) }).strict(),
  exampleRequest: { promotionId: "promo-1" },
  exampleResponse: { promotionId: "promo-1", disabled: false },
});

/**
 * B114 — promotion.list: the definition roster (§10.2). Until this op the
 * admin's Promotions screen relied on saved-this-sitting memory and B108's
 * product page could only door to it. Rows carry the FULL benefit +
 * eligibility shapes — the UI derives its own operator words; a
 * server-made "summary" string would bake presentation into the wire.
 * Optional productId answers "which promotions could touch this product":
 * targeted by eligibility.productIds, keyed by a variantKey of the
 * product, or unscoped (no product predicate = applies to every product).
 * Disabled rows stay listed with the flag — the roster is the truth, not
 * the active set. Back-office read, promotion.performance's seat.
 */
export const promotionList = readOp({
  name: "promotion.list",
  permission: "catalog:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { productId: zId.optional() },
  response: z
    .object({
      promotions: z.array(
        z
          .object({
            promotionId: zId,
            name: z.string().min(1),
            eligibility: zEligibility,
            benefit: zBenefit,
            disabled: z.boolean(),
            createdAt: zEpochMs,
            updatedAt: zEpochMs,
          })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: { productId: "p-1" },
  exampleResponse: {
    promotions: [
      {
        promotionId: "promo-1",
        name: "Summer 10",
        eligibility: { code: "SUMMER10", channelIds: ["web"], totalCap: 1000 },
        benefit: { type: "PERCENT_OFF", bps: 1000 },
        disabled: false,
        createdAt: 1_780_000_000_000,
        updatedAt: 1_780_000_000_000,
      },
    ],
  },
});

/**
 * B74 R6 (docs/OPERATIONS.md §7 R6; §10) — promotion.performance: "is
 * SUMMER10 paying for itself?" off the FROZEN checkout facts (Inv 11).
 * Rows per (promotionId, currency) over orders whose sale STOOD
 * (CONFIRMED + its refund descendants; abandoned checkouts are an Inv 13
 * cap question, not performance) with createdAt in [from, to] inclusive.
 * uses = distinct sold orders; discountMinor is POSITIVE integer minor
 * units; attributedRevenueMinor = the nets of the lines the promotion
 * touched. Refund netting deliberately stays report.series' question.
 * An unknown promotionId yields no rows, uniform with an unused one
 * (Inv 10 — no oracle for which ids exist). Books audience (ledger:read,
 * MANAGER/ADMIN); the CAPABILITY-opened set stays the four report ops.
 */
export const promotionPerformance = readOp({
  name: "promotion.performance",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {
    promotionId: zId.optional(),
    from: zEpochMs,
    to: zEpochMs,
  },
  response: z
    .object({
      rows: z.array(
        z
          .object({
            promotionId: zId,
            currency: z.string().regex(/^[A-Z]{3}$/),
            uses: z.number().int().nonnegative(),
            discountMinor: z.number().int().nonnegative(),
            attributedRevenueMinor: z.number().int().nonnegative(),
          })
          .strict(),
      ),
      /**
       * B594 (6) — the automatic pricing rules (policy tiers: `occ>=80`,
       * `within7d`) that priced lines in the same sold orders: uses, the
       * SIGNED delta, the nets they priced. Absent when a promotionId narrows
       * the read (a rule is not a promotion) and from an older stage.
       */
      rules: z
        .array(
          z
            .object({
              tier: z.string().min(1),
              currency: z.string().regex(/^[A-Z]{3}$/),
              uses: z.number().int().nonnegative(),
              adjustMinor: z.number().int(),
              attributedRevenueMinor: z.number().int().nonnegative(),
            })
            .strict(),
        )
        .optional(),
    })
    .strict(),
  exampleRequest: { promotionId: "promo-1", from: 1_780_000_000_000, to: 1_780_086_400_000 },
  exampleResponse: { rows: [{ promotionId: "promo-1", currency: "USD", uses: 12, discountMinor: 12_000, attributedRevenueMinor: 108_000 }] },
});

export const pricingPolicySet = writeOp({
  name: "pricing.policy.set",
  permission: "catalog:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    productId: zId.optional(),
    slotId: zId.optional(),
    policy: z
      .object({
        kind: z.enum(["OCCUPANCY", "TIME"]),
        tiers: z.array(z.object({ threshold: z.number().int().nonnegative(), adjustBps: z.number().int() }).strict()).min(1),
      })
      .strict(),
  },
  response: z.object({ policyId: zId }).strict(),
  exampleRequest: { productId: "p-1", policy: { kind: "OCCUPANCY", tiers: [{ threshold: 80, adjustBps: 500 }] } },
  exampleResponse: { policyId: "pol-1" },
});
